import type {
  ActionHistoryEntry,
  ArchiveReason,
  FeedSignal,
  SignalContent,
  SignalType,
  VerificationStatus,
} from "../types";
import { tierForScore } from "../tier";
import { breakdownForScore } from "../score";
import type {
  CanonicalPractice,
  ClassifiedPractice,
  PracticeClassification,
  PracticeMembershipBasis,
} from "../practice-lens-ui";

/**
 * Deterministic mock fixtures (spec §8). Covers every §3 ownership state
 * (including the assigner-visible stale case), all five archive reasons, all
 * three verification statuses, a follow-up-due signal, and 500+ rows so the
 * virtualizer + keyset pagination are exercised. No Math.random — filler rows
 * come from a seeded LCG so the dataset is byte-stable across loads.
 *
 * Viewer-owned rows carry the VIEWER_TOKEN sentinel in claimedBy/assignedTo;
 * the mock adapter swaps it for the real signed-in user id at query time, so
 * "Claimed by you" / "Assigned to you" work for whoever is logged in.
 */

export const VIEWER_TOKEN = "__VIEWER__";

// Timestamps are offsets from load time so relative labels ("3h ago") stay
// stable while absolute "First seen" dates reflect the current day.
const LOADED_AT = Date.now();
function isoAgo(minutes: number): string {
  return new Date(LOADED_AT - minutes * 60_000).toISOString();
}
function isoAhead(minutes: number): string {
  return new Date(LOADED_AT + minutes * 60_000).toISOString();
}

function content(company: string, kind: string): SignalContent {
  return {
    summary: `${company} shows a ${kind.toLowerCase()} pattern consistent with an early demand window for advisory work.`,
    why_it_matters: `The ${kind.toLowerCase()} raises near-term exposure that maps to at least one active Ankura practice, which shortens the path from signal to pursuit.`,
    recommended_action: `Confirm the internal relationship owner for ${company}, then open a coordinated outreach thread before the window closes.`,
    talk_track: `Lead with the ${kind.toLowerCase()} and the specific operational risk it creates, not a generic capabilities pitch.`,
  };
}

interface Seed {
  company: string;
  headline: string;
  score: number;
  source: string;
  practice: string;
  eventType: string;
  signalType: SignalType;
  confidence: number;
  geography: string;
  verification: VerificationStatus;
}

// One base builder keeps every fixture in the same shape.
let idCounter = 0;
function make(
  seed: Seed,
  overrides: Partial<FeedSignal> = {},
): FeedSignal {
  idCounter += 1;
  const id = overrides.id ?? `sig-${String(idCounter).padStart(4, "0")}`;
  return {
    id,
    scfScore: seed.score,
    tier: tierForScore(seed.score),
    company: seed.company,
    headline: seed.headline,
    content: content(seed.company, seed.eventType),
    signalType: seed.signalType,
    saved: false,
    scoreBreakdown: breakdownForScore(
      seed.score,
      seed.signalType === "target_account",
    ),
    source: seed.source,
    practice: seed.practice,
    eventType: seed.eventType,
    confidence: seed.confidence,
    geography: seed.geography,
    ownershipState: "unclaimed",
    lifecycleStatus: "active",
    claimedBy: null,
    claimedByName: null,
    assignedTo: null,
    assignedToName: null,
    assignedByViewer: false,
    isAssignmentStale: false,
    archiveReason: null,
    verification: seed.verification,
    followUpDueAt: null,
    firstObservedAt: isoAgo(60 * 24 * 3),
    lastObservedAt: isoAgo(180),
    deadlineAt: null,
    url: "https://example.com/source",
    ...overrides,
  };
}

/* ============================================================
   Showcase rows — one per §3 state.
   ============================================================ */
const SHOWCASE: FeedSignal[] = [
  // Unclaimed across tiers + all verification statuses
  make({
    company: "Meridian Freight Holdings",
    headline:
      "Meridian Freight warns of margin compression as new Section 301 tariffs hit its China-sourced components",
    score: 99,
    signalType: "market_intel",
    source: "Reuters",
    practice: "Geopolitical & Tariff",
    eventType: "Tariff exposure",
    confidence: 0.9,
    geography: "United States",
    verification: "verified",
  }, { id: "sig-meridian", saved: true }),
  make({
    company: "Calloway Industrial",
    headline:
      "Calloway Industrial discloses a going-concern doubt in its latest 10-Q filing",
    score: 94,
    signalType: "transformation",
    source: "SEC EDGAR",
    practice: "Turnaround & Restructuring",
    eventType: "Covenant breach",
    confidence: 0.55,
    geography: "United States",
    verification: "quarantined",
  }),
  make({
    company: "Northwind Logistics",
    headline:
      "Northwind Logistics names an interim CFO after an abrupt executive departure",
    score: 72,
    signalType: "leadership",
    source: "Company Press Release",
    practice: "Office of the CFO",
    eventType: "Leadership change",
    confidence: 0.4,
    geography: "United Kingdom",
    verification: "unverified",
  }),
  make({
    company: "Astra Materials",
    headline:
      "Astra Materials faces a shareholder suit over disclosures tied to a delayed plant restart",
    score: 58,
    signalType: "market_intel",
    source: "Law360",
    practice: "Disputes",
    eventType: "Litigation filed",
    confidence: 0.72,
    geography: "EU",
    verification: "verified",
  }),

  // Assigned to you
  make(
    {
      company: "Bluewater Chemical",
      headline:
        "Bluewater Chemical is named in a federal environmental enforcement action",
      score: 96,
      signalType: "target_account",
      source: "Federal Register",
      practice: "Compliance",
      eventType: "Regulatory action",
      confidence: 0.8,
      geography: "United States",
      verification: "verified",
    },
    {
      id: "sig-bluewater",
      ownershipState: "assigned",
      lifecycleStatus: "assigned",
      assignedTo: VIEWER_TOKEN,
      assignedToName: null,
      assignedByViewer: false,
    },
  ),
  // Assigned to another user
  make(
    {
      company: "Cedar Point Energy",
      headline:
        "Cedar Point Energy restructures its revolving credit facility ahead of a covenant test",
      score: 69,
      signalType: "transformation",
      source: "Bloomberg",
      practice: "Turnaround & Restructuring",
      eventType: "Restructuring",
      confidence: 0.6,
      geography: "United States",
      verification: "verified",
    },
    {
      ownershipState: "assigned",
      lifecycleStatus: "assigned",
      assignedTo: "user-dara",
      assignedToName: "Dara Okafor",
    },
  ),
  // Assigned to another user + STALE, viewer is the assigner/admin (dot shows)
  make(
    {
      company: "Halberd Aerospace",
      headline:
        "Halberd Aerospace loses a major defense subcontract after a program cancellation",
      score: 93,
      signalType: "m_and_a",
      source: "Politico Pro",
      practice: "Disputes",
      eventType: "Litigation filed",
      confidence: 0.75,
      geography: "United States",
      verification: "verified",
    },
    {
      ownershipState: "assigned",
      lifecycleStatus: "assigned",
      assignedTo: "user-marco",
      assignedToName: "Marco Reyes",
      isAssignmentStale: true,
      assignedByViewer: true,
    },
  ),
  // Assigned to another user + STALE, viewer is NOT assigner/admin (dot hidden)
  make(
    {
      company: "Sterling Retail Group",
      headline:
        "Sterling Retail Group draws down its full credit line amid a liquidity squeeze",
      score: 91,
      signalType: "transformation",
      source: "S&P Global",
      practice: "Office of the CFO",
      eventType: "Covenant breach",
      confidence: 0.65,
      geography: "United States",
      verification: "quarantined",
    },
    {
      ownershipState: "assigned",
      lifecycleStatus: "assigned",
      assignedTo: "user-priya",
      assignedToName: "Priya Nair",
      isAssignmentStale: true,
      assignedByViewer: false,
    },
  ),

  // Claimed by you
  make(
    {
      company: "Ironwood Manufacturing",
      headline:
        "Ironwood Manufacturing announces a plant closure and a workforce reduction",
      score: 97,
      signalType: "transformation",
      source: "The Wall Street Journal",
      practice: "Turnaround & Restructuring",
      eventType: "Restructuring",
      confidence: 0.88,
      geography: "United States",
      verification: "verified",
    },
    {
      id: "sig-ironwood",
      ownershipState: "claimed",
      claimedBy: VIEWER_TOKEN,
      claimedByName: null,
      saved: true,
    },
  ),
  // Claimed by another user
  make(
    {
      company: "Vantage Payments",
      headline:
        "Vantage Payments confirms a data breach affecting merchant records",
      score: 66,
      signalType: "market_intel",
      source: "Reuters",
      practice: "Cyber & Data",
      eventType: "Regulatory action",
      confidence: 0.5,
      geography: "Global",
      verification: "unverified",
    },
    {
      ownershipState: "claimed",
      claimedBy: "user-lena",
      claimedByName: "Lena Cho",
    },
  ),
  // Follow-up due (claimed by you, outstanding follow_up_set)
  make(
    {
      company: "Quantum Foods",
      headline:
        "Quantum Foods recalls a product line after a supplier contamination finding",
      score: 95,
      signalType: "market_intel",
      source: "Bloomberg",
      practice: "Forensics & Investigations",
      eventType: "Regulatory action",
      confidence: 0.82,
      geography: "United States",
      verification: "verified",
    },
    {
      id: "sig-quantum",
      ownershipState: "claimed",
      claimedBy: VIEWER_TOKEN,
      claimedByName: null,
      saved: true,
      followUpDueAt: isoAhead(60 * 24 * 2),
    },
  ),
  // Second follow-up due, nearer term
  make(
    {
      company: "Beacon Health Systems",
      headline:
        "Beacon Health Systems misses bond payment as patient volumes decline",
      score: 92,
      signalType: "transformation",
      source: "S&P Global",
      practice: "Office of the CFO",
      eventType: "Covenant breach",
      confidence: 0.7,
      geography: "United States",
      verification: "verified",
    },
    {
      ownershipState: "claimed",
      claimedBy: VIEWER_TOKEN,
      claimedByName: null,
      followUpDueAt: isoAhead(60 * 20),
    },
  ),
];

/* ============================================================
   Archived rows — one per archive reason.
   ============================================================ */
interface ArchivedSeed extends Seed {
  reason: ArchiveReason;
  claimedByViewer: boolean;
}
const ARCHIVED_SEEDS: ArchivedSeed[] = [
  {
    company: "Pinnacle Textiles",
    headline: "Pinnacle Textiles shifts sourcing away from tariff-exposed suppliers",
    score: 74,
    signalType: "transformation",
    source: "Reuters",
    practice: "Geopolitical & Tariff",
    eventType: "Tariff exposure",
    confidence: 0.6,
    geography: "United States",
    verification: "verified",
    reason: "no_strategic_fit",
    claimedByViewer: true,
  },
  {
    company: "Grayson Auto Parts",
    headline: "Grayson Auto Parts settles a supplier dispute out of court",
    score: 63,
    signalType: "market_intel",
    source: "Law360",
    practice: "Disputes",
    eventType: "Litigation filed",
    confidence: 0.5,
    geography: "United States",
    verification: "unverified",
    reason: "bad_timing",
    claimedByViewer: false,
  },
  {
    company: "Solace Pharma",
    headline: "Solace Pharma completes a routine leadership transition",
    score: 55,
    signalType: "leadership",
    source: "Company Press Release",
    practice: "Office of the CFO",
    eventType: "Leadership change",
    confidence: 0.45,
    geography: "EU",
    verification: "verified",
    reason: "not_relevant",
    claimedByViewer: true,
  },
  {
    company: "Draytek Semiconductors",
    headline: "Draytek Semiconductors reaffirms guidance after a brief supply scare",
    score: 81,
    signalType: "market_intel",
    source: "Bloomberg",
    practice: "Cyber & Data",
    eventType: "Earnings miss",
    confidence: 0.7,
    geography: "APAC",
    verification: "quarantined",
    reason: "already_engaged",
    claimedByViewer: false,
  },
  {
    company: "Willow Creek Utilities",
    headline: "Willow Creek Utilities closes a refinancing on favorable terms",
    score: 68,
    signalType: "transformation",
    source: "S&P Global",
    practice: "Turnaround & Restructuring",
    eventType: "Restructuring",
    confidence: 0.65,
    geography: "United States",
    verification: "verified",
    reason: "other",
    claimedByViewer: true,
  },
];
const ARCHIVED: FeedSignal[] = ARCHIVED_SEEDS.map((seed) =>
  make(seed, {
    ownershipState: "claimed",
    lifecycleStatus: "archived",
    claimedBy: seed.claimedByViewer ? VIEWER_TOKEN : "user-lena",
    claimedByName: seed.claimedByViewer ? null : "Lena Cho",
    archiveReason: seed.reason,
  }),
);

/* ============================================================
   Filler rows — seeded LCG to reach 500+ non-archived signals.
   ============================================================ */
const COMPANIES = [
  "Aldridge Steel",
  "Brightline Rail",
  "Corveth Biotech",
  "Delmar Shipping",
  "Everest Mining",
  "Fairhaven Insurance",
  "Granite Peak REIT",
  "Harmon Media",
  "Ionis Robotics",
  "Juniper Telecom",
  "Kestrel Airlines",
  "Larkspur Cosmetics",
  "Monarch Hospitality",
  "Nexus Data Centers",
  "Orion Petroleum",
  "Peregrine Capital",
  "Quill Publishing",
  "Redstone Cement",
  "Silverline Apparel",
  "Tidewater Ports",
  "Umbra Optics",
  "Vertex Packaging",
  "Wexford Distilleries",
  "Xander Motors",
  "Yarrow Agriculture",
  "Zephyr Wind",
];
const SOURCES = [
  "Reuters",
  "Bloomberg",
  "SEC EDGAR",
  "Law360",
  "Federal Register",
  "S&P Global",
  "Politico Pro",
  "The Wall Street Journal",
];
const PRACTICES = [
  "Turnaround & Restructuring",
  "Disputes",
  "Forensics & Investigations",
  "Cyber & Data",
  "Office of the CFO",
  "Geopolitical & Tariff",
  "Transaction Advisory",
  "Compliance",
];
const EVENT_TYPES = [
  "Leadership change",
  "Regulatory action",
  "Litigation filed",
  "Restructuring",
  "Tariff exposure",
  "Earnings miss",
  "M&A activity",
  "Covenant breach",
];
const GEOS = [
  "United States",
  "EU",
  "United Kingdom",
  "APAC",
  "LATAM",
  "Global",
];
const VERIFS: VerificationStatus[] = [
  "verified",
  "verified",
  "verified",
  "quarantined",
  "unverified",
];
// Every signal type appears in the filler so each filter chip has content.
const SIGNAL_TYPES: SignalType[] = [
  "rfp",
  "hiring",
  "m_and_a",
  "transformation",
  "leadership",
  "market_intel",
  "target_account",
];
const OTHER_USERS = [
  { id: "user-dara", name: "Dara Okafor" },
  { id: "user-lena", name: "Lena Cho" },
  { id: "user-marco", name: "Marco Reyes" },
  { id: "user-priya", name: "Priya Nair" },
];

function lcg(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 0xffffffff;
  };
}

/** Deterministic pool access that satisfies noUncheckedIndexedAccess. */
function pick<T>(arr: readonly T[], i: number): T {
  const v = arr[((i % arr.length) + arr.length) % arr.length];
  if (v === undefined) throw new Error("empty fixture pool");
  return v;
}

function buildFiller(count: number): FeedSignal[] {
  const rand = lcg(0x5f3d2c17);
  const rows: FeedSignal[] = [];
  for (let i = 0; i < count; i += 1) {
    const company = pick(COMPANIES, i);
    const eventType = pick(EVENT_TYPES, Math.floor(rand() * EVENT_TYPES.length));
    const signalType = pick(SIGNAL_TYPES, Math.floor(rand() * SIGNAL_TYPES.length));
    // Cap filler at 90 so the curated showcase rows (91–99) lead the feed and
    // their varied states are visible at the top, not buried under score ties.
    const score = 40 + Math.floor(rand() * 51); // 40–90
    const seed: Seed = {
      company,
      headline: `${company} reports a ${eventType.toLowerCase()} that widens its near-term advisory exposure`,
      score,
      signalType,
      source: pick(SOURCES, Math.floor(rand() * SOURCES.length)),
      practice: pick(PRACTICES, Math.floor(rand() * PRACTICES.length)),
      eventType,
      confidence: Math.round(rand() * 100) / 100,
      geography: pick(GEOS, Math.floor(rand() * GEOS.length)),
      verification: pick(VERIFS, Math.floor(rand() * VERIFS.length)),
    };
    // Distribute ownership: ~55% unclaimed, ~25% assigned, ~20% claimed.
    const roll = rand();
    let overrides: Partial<FeedSignal> = {
      firstObservedAt: isoAgo(60 * 24 * (1 + Math.floor(rand() * 30))),
      lastObservedAt: isoAgo(30 + Math.floor(rand() * 60 * 24 * 5)),
    };
    if (roll > 0.8) {
      const claimViewer = rand() > 0.6;
      const u = pick(OTHER_USERS, Math.floor(rand() * OTHER_USERS.length));
      overrides = {
        ...overrides,
        ownershipState: "claimed",
        claimedBy: claimViewer ? VIEWER_TOKEN : u.id,
        claimedByName: claimViewer ? null : u.name,
      };
    } else if (roll > 0.55) {
      const assignViewer = rand() > 0.7;
      const u = pick(OTHER_USERS, Math.floor(rand() * OTHER_USERS.length));
      const stale = rand() > 0.75;
      overrides = {
        ...overrides,
        ownershipState: "assigned",
        lifecycleStatus: "assigned",
        assignedTo: assignViewer ? VIEWER_TOKEN : u.id,
        assignedToName: assignViewer ? null : u.name,
        isAssignmentStale: stale,
        assignedByViewer: stale && rand() > 0.5,
      };
    }
    rows.push(make(seed, overrides));
  }
  return rows;
}

const FILLER = buildFiller(520);

/** Full mock dataset: showcase + archived + filler. */
export const MOCK_SIGNALS: FeedSignal[] = [...SHOWCASE, ...ARCHIVED, ...FILLER];

/* ============================================================
   Practice lens (2W) — explicit canonical registry + classifications.

   Mirrors the backend's ten canonical peer practices for a realistic mock
   experience, but this list is the mock's OWN explicit fixture, never derived
   from or coupled to the live registry: the live adapter reads
   list_signal_practices instead.
   ============================================================ */

export const MOCK_PRACTICE_REGISTRY: CanonicalPractice[] = [
  { practiceKey: "te", displayName: "Transformation Execution", displayOrder: 1 },
  { practiceKey: "vc", displayName: "Value Creation", displayOrder: 2 },
  { practiceKey: "latam", displayName: "LatAM", displayOrder: 3 },
  {
    practiceKey: "jvp",
    displayName: "Joint Ventures & Partnerships",
    displayOrder: 4,
  },
  {
    practiceKey: "tr",
    displayName: "Turnaround & Restructuring",
    displayOrder: 5,
  },
  { practiceKey: "pi_healthcare", displayName: "PI Healthcare", displayOrder: 6 },
  {
    practiceKey: "tas",
    displayName: "Transaction Advisory Services",
    displayOrder: 7,
  },
  { practiceKey: "ocfo", displayName: "Office of the CFO", displayOrder: 8 },
  { practiceKey: "digital", displayName: "Digital", displayOrder: 9 },
  { practiceKey: "eu", displayName: "EU", displayOrder: 10 },
];

function classifiedPractice(
  practiceKey: CanonicalPractice["practiceKey"],
  bases: PracticeMembershipBasis[],
): ClassifiedPractice {
  const practice = MOCK_PRACTICE_REGISTRY.find(
    (p) => p.practiceKey === practiceKey,
  );
  if (!practice) throw new Error(`unknown mock practice ${practiceKey}`);
  return { ...practice, bases };
}

function classification(...entries: ClassifiedPractice[]): PracticeClassification {
  return {
    practices: [...entries].sort((a, b) => a.displayOrder - b.displayOrder),
    automatedRouting: "not_available",
  };
}

const NOT_YET_ROUTED: PracticeClassification = {
  practices: [],
  automatedRouting: "not_available",
};

/**
 * Explicit per-signal classification (2W), keyed by signal id. Deliberately
 * INDEPENDENT of FeedSignal.practice (the legacy free-text display string on
 * every fixture row above) — governed practice membership is its own
 * hand-authored map here, never derived from it. A few named showcase
 * signals get a fixed, hand-picked classification (including one "automated"
 * basis and one explicit "unavailable" null, to exercise the full render
 * matrix); every other id falls back to a deterministic default below.
 */
const EXPLICIT_CLASSIFICATIONS: Record<string, PracticeClassification | null> = {
  // Multi-practice, and one membership with BOTH bases.
  "sig-meridian": classification(
    classifiedPractice("te", ["curator", "automated"]),
    classifiedPractice("tr", ["curator"]),
  ),
  // Automated-only membership (no curator inclusion at all).
  "sig-bluewater": classification(classifiedPractice("vc", ["automated"])),
  // Explicit "not yet routed" — an honest empty classification, not absence.
  "sig-ironwood": NOT_YET_ROUTED,
  // Explicit "unavailable" — the null case, distinct from an empty list.
  "sig-quantum": null,
};

/** Deterministic LCG, seeded independently of the filler-content LCG above so
 *  classification assignment cannot accidentally correlate with the legacy
 *  `practice` display string. */
function classificationLcg(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s * 1103515245 + 12345) >>> 0;
    return s / 0xffffffff;
  };
}

function buildDefaultClassifications(
  ids: readonly string[],
): Record<string, PracticeClassification> {
  const rand = classificationLcg(0x2f6e3a91);
  const out: Record<string, PracticeClassification> = {};
  for (const id of ids) {
    const roll = rand();
    if (roll < 0.45) {
      out[id] = NOT_YET_ROUTED;
      continue;
    }
    const count = roll < 0.85 ? 1 : 2;
    const chosen = new Set<number>();
    while (chosen.size < count) {
      chosen.add(Math.floor(rand() * MOCK_PRACTICE_REGISTRY.length));
    }
    const registry = MOCK_PRACTICE_REGISTRY;
    out[id] = classification(
      ...[...chosen].map((i) => {
        const practice = registry[i];
        if (!practice) throw new Error("empty mock practice registry");
        return classifiedPractice(practice.practiceKey, ["curator"]);
      }),
    );
  }
  return out;
}

/**
 * Effective classification for every mock signal, explicit-first. Mock
 * filtering (MockSignalFeedAdapter) and mock detail (getSignal) read ONLY
 * this map — never FeedSignal.practice.
 */
export const MOCK_PRACTICE_CLASSIFICATIONS: Record<
  string,
  PracticeClassification | null
> = {
  ...buildDefaultClassifications(
    MOCK_SIGNALS.map((s) => s.id).filter((id) => !(id in EXPLICIT_CLASSIFICATIONS)),
  ),
  ...EXPLICIT_CLASSIFICATIONS,
};

/**
 * Pre-seeded action history for a few stable-id signals so the timeline (M18)
 * renders populated states out of the box: system (mono, hollow marker) and
 * human (filled marker) actors, alongside the future follow-up pinned from the
 * signal's followUpDueAt. Chronological order (oldest first); the timeline
 * reverses to newest-first at render.
 */
export const SEEDED_HISTORY: Record<string, Omit<ActionHistoryEntry, "id">[]> = {
  "sig-quantum": [
    { actorType: "human", actorName: "You", verb: "claim", detail: null, at: isoAgo(60 * 30) },
    {
      actorType: "system",
      actorName: "Signal Engine",
      verb: "relationship_check_run",
      detail: null,
      at: isoAgo(60 * 28),
    },
    {
      actorType: "system",
      actorName: "Signal Engine",
      verb: "tariff_scan_run",
      detail: null,
      at: isoAgo(60 * 26),
    },
    {
      actorType: "human",
      actorName: "You",
      verb: "follow_up_set",
      detail: null,
      at: isoAgo(60 * 3),
    },
  ],
  "sig-ironwood": [
    { actorType: "human", actorName: "You", verb: "claim", detail: null, at: isoAgo(60 * 12) },
    {
      actorType: "system",
      actorName: "Signal Engine",
      verb: "relationship_check_run",
      detail: null,
      at: isoAgo(60 * 10),
    },
  ],
};
